import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import logo from '../../asssets/images/maskable_icon_x192.png';
import { getFormattedVersion } from '../../config/version';
import './styles.css';

const SplashScreen = ({ onHide, message = 'Carregando...', onComplete }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [currentMessage, setCurrentMessage] = useState(message);

  useEffect(() => {
    // Adicionar classe ao body para prevenir scroll
    if (isVisible) {
      document.body.classList.add('splash-active');
    } else {
      document.body.classList.remove('splash-active');
    }

    // Cleanup quando o componente for desmontado
    return () => {
      document.body.classList.remove('splash-active');
    };
  }, [isVisible]);

  useEffect(() => {
    // Detecta se é PWA instalado e dispositivo móvel
    const isPWAInstalled = window.matchMedia('(display-mode: standalone)').matches || 
                          window.navigator.standalone === true ||
                          document.referrer.includes('android-app://');
    
    const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const isSmallScreen = window.innerWidth <= 768 && window.innerHeight <= 1024;
    
    const loadingMessages = [
      'Inicializando aplicação...',
      'Verificando conectividade...',
      'Carregando recursos...',
      'Configurando ambiente...',
      'Quase pronto!'
    ];
    
    // Ajusta velocidade baseado no dispositivo
    let progressSpeed, messageSpeed;
    
    if (isPWAInstalled && isMobileDevice) {
      // PWA mobile - muito mais rápido
      progressSpeed = 150;
      messageSpeed = 400;
    } else if (isMobileDevice || isSmallScreen) {
      // Mobile browser - rápido
      progressSpeed = 200;
      messageSpeed = 500;
    } else {
      // Desktop - velocidade normal
      progressSpeed = 250;
      messageSpeed = 600;
    }
    
    // Simula progresso de carregamento
    const progressInterval = setInterval(() => {
      setLoadingProgress(prev => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          return 100;
        }
        // Progresso mais realístico - mais rápido no início, mais lento no final
        const increment = prev < 50 ? Math.random() * 20 + 5 : Math.random() * 8 + 2;
        return Math.min(prev + increment, 100);
      });
    }, progressSpeed);

    // Atualiza mensagens durante o carregamento
    const messageInterval = setInterval(() => {
      setCurrentMessage(prev => {
        const currentIndex = loadingMessages.indexOf(prev);
        const nextIndex = (currentIndex + 1) % loadingMessages.length;
        return loadingMessages[nextIndex];
      });
    }, messageSpeed);

    // Auto-hide com timing otimizado por dispositivo
    let hideTimer;
    let splashDuration;
    
    if (isPWAInstalled && isMobileDevice) {
      splashDuration = 1500; // Muito mais rápido no PWA mobile
    } else if (isMobileDevice || isSmallScreen) {
      splashDuration = 2000; // Rápido no mobile
    } else {
      splashDuration = 3000; // Duração normal no desktop
    }
    
    hideTimer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(() => {
        if (onHide) {
          onHide();
        }
        if (onComplete) {
          onComplete();
        }
      }, isPWAInstalled ? 400 : 600); // Animação mais rápida no PWA
    }, splashDuration);

    return () => {
      clearInterval(progressInterval);
      clearInterval(messageInterval);
      if (hideTimer) clearTimeout(hideTimer);
    };
  }, [onHide, onComplete]);

  const containerVariants = {
    initial: { opacity: 0, scale: 0.9 },
    animate: { 
      opacity: 1, 
      scale: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    },
    exit: { 
      opacity: 0, 
      scale: 1.1,
      transition: {
        duration: 0.6,
        ease: "easeIn"
      }
    }
  };

  const logoVariants = {
    initial: { scale: 0, rotate: -180 },
    animate: { 
      scale: 1, 
      rotate: 0,
      transition: {
        type: "spring",
        damping: 15,
        stiffness: 300,
        delay: 0.2
      }
    }
  };

  const textVariants = {
    initial: { opacity: 0, y: 30 },
    animate: { 
      opacity: 1, 
      y: 0,
      transition: {
        delay: 0.8,
        duration: 0.5
      }
    }
  };

  const progressVariants = {
    initial: { width: 0 },
    animate: { 
      width: `${loadingProgress}%`,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    }
  };

  if (!isVisible) return null;

  return (
    <AnimatePresence mode="wait">
      <motion.div
        className="splash-screen-container"
        variants={containerVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        key="splash"
      >
          {/* Background com gradiente animado */}
          <div className="splash-background">
            <div className="gradient-orb orb-1"></div>
            <div className="gradient-orb orb-2"></div>
            <div className="gradient-orb orb-3"></div>
          </div>

        {/* Conteúdo principal */}
        <div className="splash-content">
          {/* Logo com animação */}
          <motion.div
            className="splash-logo-container"
            variants={logoVariants}
          >
            <div className="logo-glow"></div>
            <img 
              src={logo} 
              alt="Meu Esporte" 
              className="splash-logo"
            />
            <div className="logo-pulse"></div>
          </motion.div>

          {/* Título */}
          <motion.div
            className="splash-title-container"
            variants={textVariants}
          >
            <h1 className="splash-title">Meu Esporte</h1>
            <p className="splash-subtitle">Organize suas agendas esportivas</p>
          </motion.div>

          {/* Barra de progresso */}
          <motion.div
            className="splash-progress-container"
            variants={textVariants}
          >
            <div className="progress-bar-background">
              <motion.div
                className="progress-bar-fill"
                variants={progressVariants}
              />
            </div>
            <p className="loading-message">{currentMessage}</p>
          </motion.div>

          {/* Versão */}
          <motion.div
            className="splash-version"
            variants={textVariants}
          >
            <span>{getFormattedVersion()}</span>
          </motion.div>
        </div>

        {/* Partículas flutuantes */}
        <div className="floating-particles">
          {[...Array(6)].map((_, i) => (
            <div 
              key={i} 
              className={`particle particle-${i + 1}`}
              style={{
                '--delay': `${i * 0.5}s`,
                '--duration': `${3 + i * 0.5}s`
              }}
            />
          ))}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default SplashScreen;
